import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LegalHRPDFExport from '../../components/legal-hr/LegalHRPDFExport';
import toast from 'react-hot-toast';
import { anthropicService } from '../../services/anthropicService';
import { supabase } from '../../lib/supabase';
import { legalHrStorageService, SavedLegalHrDocument } from '../../services/legalHrStorageService';

// Define supported countries for privacy policies
const supportedCountries = [
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'US', name: 'United States', flag: '🇺🇸' },
  { code: 'EU', name: 'European Union (GDPR)', flag: '🇪🇺' },
  { code: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺' },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
];

// Define business types for dropdown
const businessTypes = [
  'E-commerce Store',
  'SaaS Application',
  'Mobile App',
  'Blog/Content Website',
  'Personal Portfolio',
  'Corporate Website',
  'Educational Platform',
  'Non-profit Organization',
  'Online Marketplace',
  'Social Media Platform',
];

// Define data collection types
const dataCollectionTypes = [
  { id: 'contact', label: 'Contact Information', example: 'Name, email, phone number' },
  { id: 'payment', label: 'Payment Information', example: 'Credit card, billing address' },
  { id: 'device', label: 'Device Information', example: 'IP address, browser type' },
  { id: 'usage', label: 'Usage Information', example: 'Pages visited, time spent' },
  { id: 'cookies', label: 'Cookies & Similar Technologies', example: 'Tracking and analytics' },
  { id: 'location', label: 'Location Information', example: 'GPS data, country' },
];

const PrivacyPolicyGenerator: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [policyGenerated, setPolicyGenerated] = useState(false);
  
  // Form state
  const [businessName, setBusinessName] = useState('');
  const [businessWebsite, setBusinessWebsite] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [businessEmail, setBusinessEmail] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('GB');
  const [dataCollected, setDataCollected] = useState<string[]>(['contact', 'cookies']);
  const [thirdPartySharing, setThirdPartySharing] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [policyText, setPolicyText] = useState('');
  const [latestDocument, setLatestDocument] = useState<SavedLegalHrDocument | null>(null);

  // Redirect if not logged in
  React.useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  if (!user) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  const handleDataCollectionChange = (dataType: string) => {
    if (dataCollected.includes(dataType)) {
      setDataCollected(dataCollected.filter(item => item !== dataType));
    } else {
      setDataCollected([...dataCollected, dataType]);
    }
  };

  const generatePolicy = async () => {
    // Validation
    if (!businessName) {
      toast.error('Please enter your business name');
      return;
    }
    
    if (!businessWebsite) {
      toast.error('Please enter your business website');
      return;
    }
    
    if (!businessType) {
      toast.error('Please select your business type');
      return;
    }
    
    if (!businessEmail) {
      toast.error('Please enter a contact email');
      return;
    }
    
    if (dataCollected.length === 0) {
      toast.error('Please select at least one type of data you collect');
      return;
    }

    setGenerating(true);
    
    try {
      // Get the country name from the selected country code
      const selectedCountryData = supportedCountries.find(country => country.code === selectedCountry);
      const countryName = selectedCountryData ? selectedCountryData.name : 'United Kingdom';
      
      // Get current user ID from Supabase
      const { data } = await supabase.auth.getUser();
      const userId = data.user?.id;
      
      if (!userId) {
        throw new Error('User ID not available. Please log in again.');
      }

      // Format the collected data types into a readable list
      const dataTypesCollected = dataCollectionTypes
        .filter(type => dataCollected.includes(type.id))
        .map(type => `${type.label} (${type.example})`)
        .join('\n- ');

      // Create a comprehensive prompt for Anthropic
      const prompt = `
Generate a comprehensive, professional Privacy Policy for a ${businessType} called "${businessName}" (${businessWebsite}).

The business is primarily operating in ${countryName} and the privacy policy must comply with all relevant laws and regulations in that jurisdiction.

The business collects the following types of data:
- ${dataTypesCollected}

${thirdPartySharing ? 'The business DOES share data with third parties including service providers, partners, or advertisers.' : 'The business does NOT share data with third parties.'}

Contact email for privacy inquiries: ${businessEmail}

Please format the privacy policy in markdown, with appropriate sections, headings, and comprehensive coverage of all required legal elements for ${countryName}.

Make sure to include specific legal requirements for ${countryName} such as ${selectedCountry === 'EU' ? 'GDPR compliance, data subject rights, and legal basis for processing' : selectedCountry === 'US' ? 'CCPA/CPRA compliance if applicable, opt-out rights, and data sale disclosures' : selectedCountry === 'CA' ? 'PIPEDA compliance' : selectedCountry === 'GB' ? 'UK GDPR and Data Protection Act 2018 compliance' : 'applicable data protection regulations'}.

Include a "Last Updated" date with today's date.
`;

      // Make the actual API call to Anthropic through our service
      const response = await anthropicService.generateText(prompt);
      
      // Use the generated policy text from the API
      const generatedPolicyText = `
# Privacy Policy for ${businessName}

## Last Updated: ${new Date().toLocaleDateString()}

Thank you for choosing to be part of our community at ${businessName} ("Company", "we", "us", or "our"). We are committed to protecting your personal information and your right to privacy. If you have any questions or concerns about our policy, or our practices with regards to your personal information, please contact us at ${businessEmail}.

This Privacy Policy applies to all information collected through our website (${businessWebsite}), and/or any related services, sales, marketing or events (we refer to them collectively in this Privacy Policy as the "Services").

## What Information Do We Collect?

${dataCollected.includes('contact') ? '### Personal Information\nWe collect personal information that you voluntarily provide to us when registering for our Services, expressing an interest in obtaining information about us or our products and services, or otherwise contacting us. The personal information we collect may include names, email addresses, and contact information.' : ''}

${dataCollected.includes('payment') ? '### Payment Data\nWe collect data necessary to process your payment if you make purchases, such as your payment instrument number (such as a credit card number), and the security code associated with your payment instrument. All payment data is stored by our payment processor and you should review its privacy policies and contact the payment processor directly to respond to your questions.' : ''}

${dataCollected.includes('device') ? '### Technical Information\nWe automatically collect certain information when you visit, use or navigate our Services. This information does not reveal your specific identity (like your name or contact information) but may include device and usage information, such as your IP address, browser and device characteristics, operating system, language preferences, referring URLs, device name, country, location, information about how and when you use our Services and other technical information.' : ''}

${dataCollected.includes('cookies') ? '### Cookies and Similar Technologies\nWe may use cookies and similar tracking technologies to access or store information. Specific information about how we use such technologies and how you can refuse certain cookies is set out in our Cookie Policy.' : ''}

${thirdPartySharing ? '## How Do We Share Your Information?\nWe may process or share data based on the following legal basis:\n- Consent: We may process your data if you have given us specific consent to use your personal information for a specific purpose.\n- Legitimate Interests: We may process your data when it is reasonably necessary to achieve our legitimate business interests.\n- Performance of a Contract: Where we have entered into a contract with you, we may process your personal information to fulfill the terms of our contract.\n- Legal Obligations: We may disclose your information where we are legally required to do so in order to comply with applicable law, governmental requests, a judicial proceeding, court order, or legal process, such as in response to a court order or a subpoena.\n- Vital Interests: We may disclose your information where we believe it is necessary to investigate, prevent, or take action regarding potential violations of our policies, suspected fraud, situations involving potential threats to the safety of any person and illegal activities, or as evidence in litigation in which we are involved.' : '## How Do We Share Your Information?\nWe do not share or sell your personal information with third parties.'}

## ${selectedCountry === 'EU' ? 'GDPR - Your Data Protection Rights Under The General Data Protection Regulation' : 'Your Data Rights'}

${selectedCountry === 'EU' ? 
  'If you are a resident of the European Union (EU) and European Economic Area (EEA), you have certain data protection rights under the GDPR. We aim to take reasonable steps to allow you to correct, amend, delete, or limit the use of your Personal Information.\n\nIn certain circumstances, you have the following data protection rights:\n- The right to access, update or to delete your Personal Information\n- The right of rectification\n- The right to object\n- The right of restriction\n- The right to data portability\n- The right to withdraw consent' 
  : 
  'Depending on your location and applicable laws, you may have various rights regarding your personal information, such as the right to access, correct, or delete your personal data.'}

## Changes to This Privacy Policy

We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top of this Privacy Policy.

## Contact Us

If you have any questions about this Privacy Policy, please contact us at ${businessEmail}.
      `;
      
      setPolicyText(response);
      setLatestDocument(null);
      setPolicyGenerated(true);
      toast.success('Privacy Policy generated successfully!');
    } catch (error) {
      console.error('Error generating privacy policy:', error);
      toast.error('Failed to generate privacy policy. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyToClipboard = () => {
    if (policyText) {
      navigator.clipboard.writeText(policyText)
        .then(() => toast.success('Privacy policy copied to clipboard'))
        .catch((err) => {
          console.error('Failed to copy:', err);
          toast.error('Failed to copy text');
        });
    } else {
      toast.error('No content to copy');
    }
    toast.success('Copied to clipboard!');
  };

  const handlePdfExportComplete = async ({ blob }: { filename: string; blob: Blob }) => {
    if (!user?.id) {
      toast.error('You must be logged in to save the privacy policy');
      return;
    }

    try {
      const saved = await legalHrStorageService.saveLegalHrDocument({
        userId: user.id,
        docType: 'privacy-policy',
        blob,
        baseName: businessName || businessWebsite || 'privacy-policy',
        effectiveDate,
        metadata: {
          businessName,
          businessWebsite,
          businessType,
          businessEmail,
          jurisdiction: selectedCountry,
          dataCollected,
          thirdPartySharing,
        },
        tags: [
          `jurisdiction:${selectedCountry}`,
          thirdPartySharing ? 'third-party-sharing:yes' : 'third-party-sharing:no',
          `data-types:${dataCollected.length}`,
        ],
      });

      setLatestDocument(saved);
      toast.success(`Privacy Policy saved to workspace as version v${saved.version}`);
    } catch (error) {
      console.error('Failed to upload Privacy Policy PDF', error);
      toast.error('Failed to save Privacy Policy to storage');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header with gradient */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-blue-600">
            Privacy Policy Generator
          </h1>
          <p className="mt-3 text-xl text-gray-600 max-w-2xl mx-auto">
            Create a comprehensive privacy policy tailored to your business and compliant with multiple jurisdictions.
          </p>
        </div>

        {/* Main content */}
        <div className="bg-white shadow-xl rounded-lg overflow-hidden">
          {!policyGenerated ? (
            <div className="p-6 sm:p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left column - Basic Info */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">Business Information</h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-1">
                        Business Name*
                      </label>
                      <input
                        type="text"
                        id="businessName"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Your Company Ltd"
                        value={businessName}
                        onChange={(e) => setBusinessName(e.target.value)}
                      />
                    </div>

                    <div>
                      <label htmlFor="businessWebsite" className="block text-sm font-medium text-gray-700 mb-1">
                        Business Website*
                      </label>
                      <input
                        type="text"
                        id="businessWebsite"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://example.com"
                        value={businessWebsite}
                        onChange={(e) => setBusinessWebsite(e.target.value)}
                      />
                    </div>

                    <div>
                      <label htmlFor="businessEmail" className="block text-sm font-medium text-gray-700 mb-1">
                        Contact Email*
                      </label>
                      <input
                        type="email"
                        id="businessEmail"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        placeholder="privacy@example.com"
                        value={businessEmail}
                        onChange={(e) => setBusinessEmail(e.target.value)}
                      />
                    </div>

                    <div>
                      <label htmlFor="businessType" className="block text-sm font-medium text-gray-700 mb-1">
                        Business Type*
                      </label>
                      <select
                        id="businessType"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        value={businessType}
                        onChange={(e) => setBusinessType(e.target.value)}
                      >
                        <option value="">Select a business type</option>
                        {businessTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                        Primary Jurisdiction*
                      </label>
                      <select
                        id="country"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        value={selectedCountry}
                        onChange={(e) => setSelectedCountry(e.target.value)}
                      >
                        {supportedCountries.map((country) => (
                          <option key={country.code} value={country.code}>
                            {country.flag} {country.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="effectiveDate" className="block text-sm font-medium text-gray-700 mb-1">
                        Effective / Last Updated Date
                      </label>
                      <input
                        type="date"
                        id="effectiveDate"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        value={effectiveDate}
                        onChange={(e) => setEffectiveDate(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Right column - Data Collection */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">Data Collection</h2>
                  
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      What types of data do you collect?*
                    </label>
                    <div className="space-y-3">
                      {dataCollectionTypes.map((dataType) => (
                        <div key={dataType.id} className="flex items-start">
                          <div className="flex items-center h-5">
                            <input
                              id={dataType.id}
                              type="checkbox"
                              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              checked={dataCollected.includes(dataType.id)}
                              onChange={() => handleDataCollectionChange(dataType.id)}
                            />
                          </div>
                          <div className="ml-3 text-sm">
                            <label htmlFor={dataType.id} className="font-medium text-gray-700">
                              {dataType.label}
                            </label>
                            <p className="text-gray-500">{dataType.example}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <div className="flex items-start">
                      <div className="flex items-center h-5">
                        <input
                          id="thirdPartySharing"
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          checked={thirdPartySharing}
                          onChange={() => setThirdPartySharing(!thirdPartySharing)}
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor="thirdPartySharing" className="font-medium text-gray-700">
                          Share data with third parties
                        </label>
                        <p className="text-gray-500">Include provisions for sharing data with service providers, partners, or advertisers</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Generate button */}
              <div className="mt-8 flex justify-center">
                <button
                  onClick={generatePolicy}
                  disabled={generating}
                  className="px-6 py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white font-medium rounded-md shadow-md hover:shadow-lg transform transition hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {generating ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Generating...
                    </>
                  ) : (
                    'Generate Privacy Policy'
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="p-6 sm:p-8">
              <div className="mb-6 flex justify-between">
                <h2 className="text-xl font-semibold text-gray-800">Generated Privacy Policy</h2>
                <div className="space-x-2">
                  <button 
                    onClick={handleCopyToClipboard}
                    className="px-3 py-2 bg-gray-100 text-gray-600 font-medium rounded-md shadow hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Copy to Clipboard
                  </button>
                  <button 
                    onClick={() => {}} // Button replaced by LegalHRPDFExport component
                    className="px-3 py-2 bg-blue-50 text-blue-600 font-medium rounded-md shadow hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Download PDF
                  </button>
                  <button 
                    onClick={() => setPolicyGenerated(false)}
                    className="px-3 py-2 bg-gray-100 text-gray-600 font-medium rounded-md shadow hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Edit
                  </button>
                </div>
              </div>
              
              {/* Display the generated policy in a nicely formatted way */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 prose max-w-none">
                <div dangerouslySetInnerHTML={{ 
                  __html: policyText
                    .replace(/^# (.*)/gm, '<h1 class="text-3xl font-bold mb-4">$1</h1>')
                    .replace(/^## (.*)/gm, '<h2 class="text-2xl font-semibold mt-6 mb-3">$1</h2>')
                    .replace(/^### (.*)/gm, '<h3 class="text-xl font-medium mt-4 mb-2">$1</h3>')
                    .replace(/\n\n/g, '<p class="mb-4"></p>') 
                }} />
              </div>
              
              <div className="mt-8 text-center">
                <div className="bg-yellow-50 p-4 border border-yellow-100 rounded-md mb-4">
                  <p className="text-sm text-yellow-700">
                    <strong>Legal Disclaimer:</strong> This privacy policy is generated by AI and should be reviewed by a qualified legal professional before use. Laws and regulations vary by jurisdiction and may change over time. The generated policy may not cover all legal requirements specific to your business or jurisdiction.
                  </p>
                </div>
                <button
                  onClick={() => {
                    setPolicyGenerated(false);
                    window.scrollTo(0, 0);
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white font-medium rounded-md shadow-md hover:shadow-lg transform transition hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Generate Another Policy
                </button>
              </div>
            </div>
          )}
          
          {/* Disclaimer */}
          <div className="bg-yellow-50 p-4 border-t border-yellow-100">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Legal Disclaimer</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    This generator provides a template only and is not a substitute for professional legal advice. 
                    The generated policy should be reviewed by a qualified legal professional before implementation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyGenerator;
